import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ArrowDownRight, ArrowUpRight, ArrowRight, ChevronDown, Quote } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PROJECTS, PARTNERS, MOCK_NEWS, LOGO_URL_LIGHT } from "@/data/ekipData";
import EcosystemSchema from "@/components/ecosysteme/EcosystemSchema";

const HERO_BG =
  "https://images.pexels.com/photos/14936005/pexels-photo-14936005.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";
const FOUNDER_BG =
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=compress&cs=tinysrgb&fm=jpg&w=1200&q=80";

export default function Home() {
  const { t, lang } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen w-full overflow-hidden grain bg-ekip-navy"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ekip-navy via-ekip-navy/85 to-ekip-navy/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-ekip-navy/50 via-transparent to-ekip-navy" />

        {/* Diamond decor */}
        <div className="absolute right-8 md:right-24 top-1/2 -translate-y-1/2 hidden md:block pointer-events-none">
          <div className="relative w-[380px] h-[380px] lg:w-[520px] lg:h-[520px]">
            <div className="absolute inset-0 border border-ekip-copper/25 float-slow" />
            <div className="absolute inset-8 border border-ekip-copper/15 float-slow" style={{ animationDelay: "1s" }} />
            <div className="absolute inset-16 border border-ekip-copper/10 float-slow" style={{ animationDelay: "2s" }} />
            <div className="absolute top-1/2 left-1/2 w-4 h-4 bg-ekip-copper" style={{ transform: "translate(-50%,-50%) rotate(45deg)" }} />
          </div>
        </div>

        <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-44 md:pt-48 pb-32 min-h-screen flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="mb-6 md:mb-8"
          >
            <span
              className="text-ekip-copper uppercase font-body"
              style={{ fontSize: "14px", fontWeight: 300, letterSpacing: "0.35em" }}
            >
              EKIP HOLDING COMPANY
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            data-testid="home-hero-title"
            className="font-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1] max-w-4xl text-white"
          >
            {t.home.hero.title1}
            <br />
            <em className="italic font-light text-ekip-copper">{t.home.hero.title2}</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            data-testid="home-hero-subtitle"
            className="mt-8 md:mt-10 max-w-2xl text-white/80 font-body font-normal leading-relaxed"
            style={{ fontSize: "18px" }}
          >
            {t.home.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link
              to="/ecosysteme"
              data-testid="home-hero-primary-cta"
              className="group inline-flex items-center gap-3 bg-ekip-copper text-white px-8 py-4 text-[13px] uppercase tracking-[0.16em] font-medium hover:bg-ekip-copper-hover transition-colors"
              style={{ borderRadius: "4px" }}
            >
              {t.home.hero.ctaPrimary}
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
            <Link
              to="/projets"
              data-testid="home-hero-secondary-cta"
              className="group inline-flex items-center gap-3 bg-transparent text-white px-8 py-4 text-[13px] uppercase tracking-[0.16em] font-medium border border-white/70 hover:bg-white hover:text-ekip-navy hover:border-white transition-colors"
              style={{ borderRadius: "4px" }}
            >
              {t.home.hero.ctaSecondary}
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </motion.div>

          {/* Animated scroll indicator */}
          <motion.a
            href="#stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.8 }}
            data-testid="home-hero-scroll"
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-white/60 hover:text-ekip-copper transition-colors"
            aria-label="Défiler"
          >
            <span className="text-[10px] uppercase tracking-[0.4em] font-medium">
              {t.home.hero.scroll}
            </span>
            <span className="scroll-chevron block w-px h-14 bg-gradient-to-b from-transparent via-ekip-copper to-ekip-copper" />
            <ChevronDown size={20} className="animate-bounce-slow" strokeWidth={1.4} />
          </motion.a>

          {/* Slogan bottom-left */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.9 }}
            className="absolute bottom-10 left-6 md:left-10 lg:left-14 flex items-center gap-4 pointer-events-none"
          >
            <div className="h-px w-12 bg-ekip-copper" />
            <span className="font-display italic text-lg text-white/90">
              {t.common.slogan}
            </span>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <section
        id="stats"
        data-testid="home-stats"
        className="bg-ekip-cream border-y border-ekip-gold/20 py-16 md:py-24"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-5 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.home.stats.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-7 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.home.stats.title}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 border-t border-ekip-gold/20 pt-12">
            {t.home.stats.items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`home-stat-${i}`}
              >
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-bold text-5xl md:text-6xl lg:text-7xl text-ekip-copper leading-none">
                    <CountUp end={it.value} duration={2.4} enableScrollSpy scrollSpyOnce separator=" " />
                  </span>
                  <span className="font-display font-bold text-2xl md:text-3xl text-ekip-copper">
                    {it.suffix}
                  </span>
                </div>
                <div className="mt-5 h-px w-10 bg-ekip-copper" />
                <p className="mt-3 text-xs md:text-[13px] uppercase tracking-[0.24em] text-ekip-graphite font-body font-medium">
                  {it.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section
        id="ecosysteme"
        data-testid="home-ecosystem"
        className="bg-ekip-navy-deep py-20 md:py-32 overflow-hidden relative"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 relative">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.home.ecosystem.eyebrow}
              </span>
            </div>
            <div className="col-span-12 md:col-span-8">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                className="font-display font-semibold text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-white"
              >
                {t.home.ecosystem.title}
                <br />
                <em className="italic font-light text-ekip-copper">{t.home.ecosystem.titleItalic}</em>
              </motion.h2>
              <p className="mt-6 max-w-xl text-white/70 font-body font-light leading-relaxed">
                {t.home.ecosystem.subtitle}
              </p>
            </div>
          </div>

          <EcosystemSchema darkBg />

          <div className="mt-14 text-center">
            <Link
              to="/ecosysteme"
              data-testid="home-ecosystem-cta"
              className="inline-flex items-center gap-3 text-ekip-copper hover:text-white text-xs uppercase tracking-[0.3em] font-medium border-b border-ekip-copper/40 hover:border-white pb-2 transition-colors"
            >
              {t.home.ecosystem.cta}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section
        id="projets"
        data-testid="home-projects"
        className="bg-ekip-cream py-20 md:py-32"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.home.projects.eyebrow}
              </span>
            </div>
            <div className="col-span-12 md:col-span-8">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                className="font-display font-semibold text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-ekip-navy"
              >
                {t.home.projects.title}
                <br />
                <em className="italic font-light text-ekip-copper">{t.home.projects.titleItalic}</em>
              </motion.h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {PROJECTS.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`home-project-${p.slug}`}
                className="group relative h-[440px] md:h-[480px] border border-ekip-gold/20 overflow-hidden bg-ekip-navy"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-105"
                  style={{ backgroundImage: `url(${p.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ekip-navy via-ekip-navy/70 to-ekip-navy/20 group-hover:from-ekip-navy group-hover:via-ekip-navy/80 transition-all" />

                <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                  <span className="inline-flex items-center gap-2 bg-ekip-copper text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-medium">
                    {p.badge}
                  </span>
                  <ArrowUpRight size={22} strokeWidth={1.2} className="text-ekip-copper group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 px-6 md:px-8 py-8">
                  <h3 className="font-display font-semibold text-2xl md:text-3xl text-white leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-md font-body font-light">
                    {p.subtitle[lang]}
                  </p>
                  <div className="mt-5 h-px w-12 bg-ekip-copper group-hover:w-24 transition-all duration-500" />
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <Link to="/projets" data-testid="home-projects-cta" className="btn-outline">
              {t.home.projects.cta}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER QUOTE */}
      <section
        data-testid="home-quote"
        className="relative bg-ekip-navy py-20 md:py-32 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${FOUNDER_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ekip-navy via-ekip-navy/95 to-ekip-navy/70" />
        <div className="absolute -right-40 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none">
          <div className="w-[600px] h-[600px] border border-ekip-gold" style={{ transform: "rotate(45deg)" }} />
        </div>

        <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium flex items-center gap-3">
                <span className="diamond" />
                {t.home.quote.eyebrow}
              </span>
            </div>
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="col-span-12 md:col-span-8"
            >
              <Quote size={56} strokeWidth={0.9} className="text-ekip-copper mb-8" />
              <p className="font-display font-light italic text-2xl md:text-3xl lg:text-4xl leading-[1.35] text-white">
                {t.home.quote.text}
              </p>
              <div className="mt-10 flex items-center gap-4">
                <div className="h-px w-10 bg-ekip-copper" />
                <div>
                  <div className="font-display font-semibold text-white text-lg">
                    {t.home.quote.author}
                  </div>
                  <div className="text-sm text-white/70 font-body">
                    {t.home.quote.role}
                  </div>
                </div>
              </div>
            </motion.blockquote>
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section
        data-testid="home-news"
        className="bg-ekip-cream py-20 md:py-32"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.home.news.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.home.news.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {MOCK_NEWS.slice(0, 3).map((n, i) => (
              <motion.article
                key={n.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                data-testid={`home-news-${n.slug}`}
                className="group border border-ekip-gold/20 bg-white overflow-hidden hover:border-ekip-copper transition-colors"
              >
                <div className="relative h-56 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${n.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ekip-navy/50 to-transparent" />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-ekip-navy text-white px-3 py-1 text-[10px] uppercase tracking-[0.22em]">
                    {t.actualites.categories[n.category] || n.category}
                  </span>
                </div>
                <div className="p-6 md:p-8">
                  <time className="text-[10px] uppercase tracking-[0.25em] text-ekip-graphite">
                    {new Date(n.date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                  <h3 className="mt-3 font-display font-semibold text-xl text-ekip-navy leading-snug">
                    {n.title[lang]}
                  </h3>
                  <p className="mt-3 text-sm text-ekip-graphite font-body font-light leading-relaxed line-clamp-2">
                    {n.excerpt[lang]}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/actualites" data-testid="home-news-cta" className="btn-outline">
              {t.common.allNews}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section
        data-testid="home-partners"
        className="bg-white border-y border-ekip-gold/20 py-16 md:py-20 overflow-hidden"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 mb-8">
          <div className="flex items-center gap-3">
            <span className="diamond" />
            <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
              {t.home.partners.eyebrow}
            </span>
          </div>
          <h3 className="mt-3 font-display font-semibold text-2xl md:text-3xl text-ekip-navy">
            {t.home.partners.title}
          </h3>
        </div>
        <div className="marquee-track flex gap-16 whitespace-nowrap w-max">
          {[...PARTNERS, ...PARTNERS].map((p, i) => (
            <div key={i} className="flex items-center gap-6">
              <span className="diamond-outline" />
              <span className="font-display font-semibold text-xl md:text-2xl text-ekip-navy/70">
                {p}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
